/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useMe } from '@/queries/session';
import { ApiError } from '@/lib/api';

/**
 * The session gate — which does **not** decide anything.
 *
 * The server is the authority: `requireAuth` verifies the cookie on every
 * request and answers 401 when it is missing or expired. This component only
 * reacts to that answer. Re-implementing the check here would mean two opinions
 * about who is signed in, and the client's would be the one that is wrong.
 *
 * It deliberately shows nothing while the first `/auth/me` is in flight rather
 * than a spinner: the request is local and fast, and a flash of "Loading…"
 * before every board is worse than a beat of empty page.
 */
export function RequireSession() {
  const location = useLocation();
  const { isLoading, error, data } = useMe();

  if (isLoading) return null;

  if (error instanceof ApiError && error.isUnauthenticated) {
    // `state.from` so a deep link survives the round trip through Microsoft —
    // somebody opening a pasted task link should land on that task, not the board.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  /**
   * **The server could not be reached, and this used to fall through to the
   * board anyway.**
   *
   * Only the 401 was handled; every other failure — a refused connection, a 502
   * from nginx while Express restarts — rendered `<Outlet />` with no session at
   * all. What that looks like is a board that half-arrives: no name in the
   * corner, empty columns, and no statement anywhere that anything is wrong. The
   * only way out was a reload, which is precisely what Abhishek reported.
   *
   * Now it says so, and `useMe` retries every five seconds until it works — so
   * the board arrives by itself when the server does. `data &&` matters: once
   * there is a session in hand a later blip must not throw the person off the
   * board they are already using.
   */
  if (error && !data) return <Unreachable />;

  return <Outlet />;
}

/**
 * Deliberately not the `ErrorScreen`: that one is for a crash, says "it could not
 * recover", and offers links out. This is a wait, and the honest thing to show is
 * that it is still trying. No Reload button as the first offer — the app is about
 * to do this for you, and a button that races the retry teaches people the app
 * cannot fix itself.
 */
function Unreachable() {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-5">
      <div className="max-w-[380px] text-center">
        <p className="m-0 text-base font-semibold">Qollab is not answering.</p>
        <p className="mb-0 mt-1.5 text-sm leading-relaxed text-ink-muted">
          This usually means the server is restarting. Trying again every few
          seconds — the board will come back on its own.
        </p>
      </div>
    </div>
  );
}
